// Microsoft Clarity session tags (owner, 2026-11-03: "implement clarity onto the care pages so we can track all of
// these issues"). The Clarity tag itself is in public/index.html (project imex2jziru) and records register and both
// care hosts into one project, so these tags let a Clarity filter pick out care sessions and the cases we chase:
//   host        location.hostname (care.yourdoctors.online, ydo-care-stg.web.app, register.yourdoctors.online)
//   build       REACT_APP_BUILD_ID (date + git commit), when the build stamped one
//   in_app      instagram | facebook | none (Meta ad clicks open inside those apps' browsers)
//   platform    android | ios | other
//   reopened    yes when this browser had the site open less than 2 minutes before this page load, which is how a
//               closed-and-reopened in-app browser looks (Instagram on Android closed its browser mid-sign-up,
//               2026-11-03); also sends the Clarity event "reopened_within_2_min".
// Call tagClaritySession() once at start-up (src/index.js). Side effects: calls window.clarity (the tag's queue
// stub works before clarity.js loads), writes localStorage key ydo_last_seen every 15 s and when the page is
// hidden. Never throws; does nothing without window.clarity. Sends no personal data.

export const LAST_SEEN_KEY = 'ydo_last_seen'
export const REOPEN_WINDOW_MS = 2 * 60 * 1000

/**
 * Names the in-app browser from a user agent.
 *
 * @param {string} ua a user agent string.
 * @returns {'instagram'|'facebook'|'none'}
 */
export const inAppBrowser = (ua) => {
  const s = String(ua || '')
  if (/\bInstagram\b/.test(s)) return 'instagram'
  if (/FBAN\/|FBAV\/|FB_IAB\//.test(s)) return 'facebook'
  return 'none'
}

/**
 * Names the phone platform from a user agent.
 *
 * @param {string} ua a user agent string.
 * @returns {'android'|'ios'|'other'}
 */
export const platformOf = (ua) => {
  const s = String(ua || '')
  if (/\bAndroid\b/.test(s)) return 'android'
  if (/iPhone|iPad|iPod/.test(s)) return 'ios'
  return 'other'
}

/**
 * Tags this Clarity session (see the file header) and starts the last-seen heartbeat.
 *
 * @param {object} [opts]
 * @param {Window} [opts.win] the window to read and tag; defaults to the global one (tests pass a fake).
 * @param {() => number} [opts.now] clock in ms; defaults to Date.now.
 * @returns {object|null} the tags that were set, or null when Clarity is missing.
 */
export function tagClaritySession({ win = window, now = Date.now } = {}) {
  try {
    if (!win || typeof win.clarity !== 'function') return null
    const ua = (win.navigator && win.navigator.userAgent) || ''
    let last = null
    try {
      last = Number(win.localStorage.getItem(LAST_SEEN_KEY)) || null
    } catch (e) {
      last = null
    }
    const t = now()
    const tags = {
      host: (win.location && win.location.hostname) || 'unknown',
      in_app: inAppBrowser(ua),
      platform: platformOf(ua),
      reopened:
        last && t - last >= 0 && t - last < REOPEN_WINDOW_MS ? 'yes' : 'no',
    }
    if (process.env.REACT_APP_BUILD_ID)
      tags.build = process.env.REACT_APP_BUILD_ID
    Object.keys(tags).forEach((k) => win.clarity('set', k, tags[k]))
    if (tags.reopened === 'yes') win.clarity('event', 'reopened_within_2_min')

    const beat = () => {
      try {
        win.localStorage.setItem(LAST_SEEN_KEY, String(now()))
      } catch (e) {
        // Storage blocked (private mode): the reopened tag just stays "no".
      }
    }
    beat()
    if (typeof win.setInterval === 'function') win.setInterval(beat, 15000)
    if (win.document && typeof win.document.addEventListener === 'function') {
      win.document.addEventListener('visibilitychange', beat)
    }
    return tags
  } catch (e) {
    return null
  }
}
